/**
 * V108-6 setup screens — shared parts: glyphs, chips, the status pill + its three-line popover, and the
 * per-program coverage block (number, delta, four-part CoverageBar, projection). Used by the coverage
 * view (onboarding step 8), Setup and Today, so all three read the same numbers (one count source).
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CoverageBar } from '@aigovops/design-system';
import { getProgramManifest } from '@aigovops/core';
import type { EnablerItem } from '../../lib/setup-api';
import {
  coverageParts, coverageStatus, deltaText, movesNothing, nextStepLabel, pillText, projection, statusLines, STATUS_WORD, weeklyDelta, type CoverageParts, type ProgramStatusInput,
} from '../../lib/setup-model';
import type { CoverageProgram } from '../../lib/setup-api';

type GlyphKind = 'check' | 'clock' | 'alert' | 'lock' | 'eye' | 'console' | 'agent' | 'collector' | 'ext';
const PATHS: Record<GlyphKind, ReactNode> = {
  check: <polyline points="4 12.5 9.5 18 20 6" />,
  clock: <><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15.5 14" /></>,
  alert: <><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>,
  lock: <><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>,
  eye: <><path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></>,
  console: <><rect x="3" y="4" width="18" height="16" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /></>,
  agent: <><polyline points="4 17 10 11 4 5" /><line x1="12" y1="19" x2="20" y2="19" /></>,
  collector: <><rect x="3" y="4" width="18" height="7" rx="1" /><rect x="3" y="13" width="18" height="7" rx="1" /></>,
  ext: <><path d="M14 4h6v6" /><line x1="20" y1="4" x2="11" y2="13" /><path d="M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" /></>,
};
export function Glyph({ kind }: { kind: GlyphKind }) {
  return <svg className="su-ico" viewBox="0 0 24 24" aria-hidden="true">{PATHS[kind]}</svg>;
}

export function State({ tone, glyph, children }: { tone: 'progress' | 'done' | 'attention' | 'partial' | 'muted'; glyph: GlyphKind; children: ReactNode }) {
  return <span className="su-state" data-tone={tone}><Glyph kind={glyph} />{children}</span>;
}

export const programShort = (program: string) => getProgramManifest(program)?.shortLabel ?? program;

/** Unlock chips (accent tint): "+9 SOC 2". One shape for every program (Medium: chips). */
export function UnlockChips({ step }: { step: Pick<EnablerItem, 'unlocks'> }) {
  return <>{Object.entries(step.unlocks.programs).filter(([, ids]) => ids.length).map(([k, ids]) => (
    <span key={k} className="su-chip" data-kind="unlock">+{ids.length} {programShort(k)}<span className="su-sr"> objectives</span></span>
  ))}</>;
}

/** V108-8-8: what a step's evidence is called in "No GovRAMP objective uses …". */
export function evidenceNoun(code: string): string {
  if (code.startsWith('ci-gate:')) return 'CI evidence';
  if (code === 'sbom-push') return 'an SBOM';
  return "this step's evidence";
}

export const orList = (names: readonly string[]) => (names.length === 1 ? names[0]! : `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`);

/**
 * V108-8-8: why a step unlocks nothing, in plain words, instead of a bare 0: "No GovRAMP or SOC 2 objective uses
 * CI evidence." Rendered only for a step that moves nothing (`movesNothing`), and once per kind of evidence in a
 * list (later rows get NotTakenChip). Each program links to what the user can do now: its objectives that need
 * evidence a person adds (`?evidence=<p>#nosrc-<p>`, opened and focused on Setup) when it has some, else its
 * steps. More than two programs collapse to one link to the whole list.
 */
export function NotTakenLine({ step, prefix }: { step: Pick<EnablerItem, 'code' | 'unlocks' | 'objectives_unlocked'>; prefix: string }) {
  if (!movesNothing(step)) return null;
  const programs = step.unlocks.not_taken_by!;
  const text = `No ${orList(programs.map((p) => programShort(p.program)))} objective uses ${evidenceNoun(step.code)}.`;
  const links = programs.length > 2
    ? [programs.some((p) => p.has_manual !== false)
      ? { key: 'all', to: `${prefix}/setup#no-source`, label: 'Add evidence by hand' }
      : { key: 'all', to: `${prefix}/setup#steps`, label: 'See all steps' }]
    : programs.map((p) => p.has_manual !== false
      ? { key: p.program, to: `${prefix}/setup?evidence=${p.program}#nosrc-${p.program}`, label: `Add ${programShort(p.program)} evidence by hand` }
      : { key: p.program, to: `${prefix}/setup?program=${p.program}#steps`, label: `See ${programShort(p.program)} steps` });
  return (
    <p className="su-row-sub" data-part="not-taken">
      <span>{text}</span>
      {links.map((l) => <Link key={l.key} className="su-linkish" to={l.to}>{l.label}</Link>)}
    </p>
  );
}

/** V108-8-8: the short form on later rows that move nothing (the full line sits on the first one). */
export function NotTakenChip() {
  return <span className="su-chip" data-kind="none">Moves no objective yet</span>;
}

const KIND: Record<EnablerItem['actor'], [GlyphKind, string]> = {
  admin_console: ['console', 'Admin console'], repo_agent: ['agent', 'Your coding agent'], collector: ['collector', 'Collector in your network'],
};
export function ActorKind({ step }: { step: Pick<EnablerItem, 'actor' | 'effort_min'> }) {
  const [g, label] = KIND[step.actor];
  return <span className="su-kind"><Glyph kind={g} />{label} · {step.effort_min} min</span>;
}

/** A link that opens in a new tab and says so (external console links). */
export function ExtLink({ href, children, variant, onClick }: { href: string; children: ReactNode; variant?: 'primary' | 'ghost'; onClick?: () => void }) {
  return <a className="su-btn" data-variant={variant} href={href} target="_blank" rel="noopener noreferrer" onClick={onClick}>
    {children} <Glyph kind="ext" /><span className="su-sr"> (opens in a new tab)</span>
  </a>;
}

// ---------------------------------------------------------------------------------------------------
// Status pill + popover (one time unit; three lines; Esc / outside click close; focus returns)
// ---------------------------------------------------------------------------------------------------

export function StatusPill({ name, status, parts, delta, today, programHref }: {
  name: string; status: ProgramStatusInput; parts: CoverageParts; delta: number | null; today: string; programHref: string;
}) {
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const id = useId();
  const lines = statusLines(parts, status, delta, today);
  useEffect(() => {
    if (!open) return;
    pop.current?.querySelector<HTMLElement>('a, button')?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setOpen(false); btn.current?.focus(); } };
    const onDown = (e: PointerEvent) => { if (!pop.current?.contains(e.target as Node) && !btn.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onDown);
    return () => { document.removeEventListener('keydown', onKey, true); document.removeEventListener('pointerdown', onDown); };
  }, [open]);
  return (
    <span className="su-pill-wrap" data-esc-owner={open ? '' : undefined}>
      <button ref={btn} type="button" className="su-pill" data-s={status.status} aria-expanded={open} aria-controls={`${id}-pop`} aria-haspopup="dialog" onClick={() => setOpen((o) => !o)}>
        <span className="su-dot" aria-hidden="true" />{pillText(status)}<span className="su-sr">. What this means</span>
      </button>
      {open && (
        <div ref={pop} id={`${id}-pop`} className="su-pop" role="dialog" aria-labelledby={`${id}-h`}
          onBlur={(e) => { const to = e.relatedTarget as Node | null; if (to && !pop.current?.contains(to) && !btn.current?.contains(to)) setOpen(false); }}>
          <h3 id={`${id}-h`}>{name} · {STATUS_WORD[status.status]}</h3>
          <dl>
            <div><dt>The date</dt><dd>{lines.what}</dd></div>
            <div><dt>The rule</dt><dd>{lines.rule}</dd></div>
            <div><dt>What “seen” means</dt><dd>{lines.seen} <Link to={programHref}>See {name} readiness</Link></dd></div>
          </dl>
          <div className="su-pop-foot"><button type="button" className="su-linkish" onClick={() => { setOpen(false); btn.current?.focus(); }}>Close</button></div>
        </div>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------------------------------
// One program's coverage
// ---------------------------------------------------------------------------------------------------

export interface CoverageBlockProps {
  cov: CoverageProgram;
  enablers: readonly EnablerItem[];
  status: ProgramStatusInput | null;
  today: string;
  prefix: string;
  /** h3 on the coverage view / Setup; null on Today (the card names the program). */
  heading?: 'h3' | null;
  /** Coverage view + Setup show "N seen" in the legend; Today says it in the number. */
  legendSeen?: boolean;
  /** Where "need evidence you add" goes (the no-source list). */
  noSourceHref: string;
  /** AC-2: the no-source line with owners · asked (Today, at-risk cards). */
  causeLine?: ReactNode;
  showProjection?: boolean;
  /** Today's card states the number in its metric line; the block then starts at the bar. */
  hideNumber?: boolean;
}

export function ProgramCoverageBlock(p: CoverageBlockProps) {
  const parts = coverageParts(p.cov, p.enablers);
  const delta = weeklyDelta(p.cov, p.today);
  const proj = p.showProjection && p.status ? projection(parts, p.status.status) : null;
  const name = programShort(p.cov.program);
  const H = p.heading ?? null;
  const programHref = `${p.prefix}/programs/${p.cov.program}`;
  return (
    <div className="su-cov" data-program={p.cov.program}>
      {H && <div className="su-cov-top">
        <H className="su-cov-name">{name}</H>
        {p.status && <StatusPill name={name} status={p.status} parts={parts} delta={delta} today={p.today} programHref={programHref} />}
      </div>}
      {!p.hideNumber && <p className="su-cov-num">
        {parts.seen} <span className="su-of">of {parts.total} seen</span><span className="su-togo">{parts.togo} to go</span>
        {deltaText(delta) && <span className="su-just">{delta! > 0 && <span aria-hidden="true">▲ </span>}{deltaText(delta)}</span>}
      </p>}
      {parts.total > 0 && <CoverageBar label={name} total={parts.total} seen={parts.seen}
        next={parts.next ? { count: parts.next.count, title: nextStepLabel(parts.next.step) } : null}
        other={parts.other} noSource={parts.noSource} showSeenLegend={p.legendSeen}
        noSourceHref={p.noSourceHref} hideNoSourceLegend={!!p.causeLine}
        renderLink={({ href, children }) => <Link to={href}>{children}</Link>} />}
      {p.causeLine}
      {parts.partial > 0 && <p className="su-preview-line"><span className="su-chip" data-kind="partial">Partial</span>{parts.partial} more once every production account is read</p>}
      {proj && <p className="su-proj">{proj.text}{proj.stillAtRisk && <strong> · {proj.still}</strong>}</p>}
    </div>
  );
}

/** The pill's status + date from a program card (the program's own status, the same for every viewer).
 * V108-8-3: with the program's coverage, "On track" at nothing seen and under a week of history reads "Not started". */
export function statusFromCard(card: { program_status?: string; status?: string; verdict: { status: string }; next_date: { date: string; what: string } | null; target_date?: string | null } | undefined,
  cov?: Pick<CoverageProgram, 'connected' | 'trend'> | null, today?: string): ProgramStatusInput | null {
  if (!card) return null;
  const s = (card.program_status ?? card.status ?? card.verdict.status) as ProgramStatusInput['status'];
  const status = (['on_track', 'at_risk', 'off_track', 'not_started'] as const).includes(s) ? s : 'on_track';
  const base = { status, date: card.next_date?.date ?? card.target_date ?? null, dateWhat: card.next_date?.what ?? (card.target_date ? 'Target date' : null) };
  return cov ? coverageStatus(base, cov, today ?? new Date().toISOString().slice(0, 10)) : base;
}
